import { useState, useEffect } from 'react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { matchSorter } from 'match-sorter';

/**
 * Custom scaffolder field that lists GitHub repositories the current user
 * has access to in the `univerus` org, displayed in a searchable dropdown.
 *
 * Uses the user's own GitHub OAuth token (via `githubAuthApiRef`) so the
 * dropdown only shows repos they can see.  Calls the GitHub API directly
 * from the browser — no backend proxy needed.
 *
 * When `ui:options.requestUserCredentials.secretsKey` is configured in the
 * template YAML, the component also stores the user's GitHub OAuth token
 * as a template secret.  This allows downstream scaffolder steps (e.g.
 * `publish:github:pull-request`) to authenticate as the user rather than
 * using the system integration token.
 *
 * @example
 * ```yaml
 * repoName:
 *   title: Repository
 *   type: string
 *   ui:field: GitHubRepoPicker
 *   ui:options:
 *     requestUserCredentials:
 *       secretsKey: USER_OAUTH_TOKEN
 * ```
 *
 * @see Story 2.5 — TechDocs Bootstrap Scaffolder Template
 */

const GITHUB_ORG = 'univerus';

interface RepoOption {
  value: string;
  label: string;
  description: string;
}

/**
 * Fetch all non-archived, non-fork repos the user can see in the org.
 * Paginates automatically (100 per page).
 */
async function fetchUserRepos(token: string): Promise<RepoOption[]> {
  const repos: RepoOption[] = [];
  let page = 1;
  const perPage = 100;
  let hasMore = true;

  while (hasMore) {
    const res = await fetch(
      `https://api.github.com/orgs/${GITHUB_ORG}/repos?type=all&sort=name&direction=asc&per_page=${perPage}&page=${page}`,
      {
        headers: {
          Authorization: `token ${token}`,
          Accept: 'application/vnd.github+json',
        },
      },
    );

    if (!res.ok) {
      throw new Error(`GitHub API error: ${res.status} ${res.statusText}`);
    }

    const data: Array<{
      name: string;
      description: string | null;
      archived: boolean;
      fork: boolean;
    }> = await res.json();

    if (data.length === 0) {
      hasMore = false;
    } else {
      for (const repo of data) {
        if (repo.archived || repo.fork) continue;
        repos.push({
          value: repo.name,
          label: repo.name,
          description: repo.description || '',
        });
      }

      hasMore = data.length >= perPage;
      page++;
    }
  }

  return repos;
}

export const GitHubRepoPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const githubAuth = useApi(githubAuthApiRef);
  const { setSecrets } = useTemplateSecrets();
  const [repos, setRepos] = useState<RepoOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Extract the secretsKey from ui:options.requestUserCredentials (if configured)
  const secretsKey = (
    uiSchema?.['ui:options'] as
      | { requestUserCredentials?: { secretsKey?: string } }
      | undefined
  )?.requestUserCredentials?.secretsKey;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        // Request a token with read:org scope so we can list org repos.
        // If the user hasn't authorized GitHub yet, Backstage will pop up
        // the OAuth consent window automatically via OAuthRequestDialog.
        const token = await githubAuth.getAccessToken([
          'read:org',
          'repo',
          'workflow',
        ]);

        // Store the user's OAuth token as a template secret so downstream
        // steps can authenticate as the user (e.g. for PR creation).
        if (secretsKey) {
          setSecrets({ [secretsKey]: token });
        }

        const options = await fetchUserRepos(token);
        if (!cancelled) setRepos(options);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to fetch repositories',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [githubAuth, secretsKey, setSecrets]);

  const handleChange = (
    _event: React.ChangeEvent<unknown>,
    value: RepoOption | null,
  ) => {
    onChange(value?.value || '');
  };

  // Custom filter function using match-sorter for fuzzy search
  const filterOptions = (
    options: RepoOption[],
    { inputValue }: { inputValue: string },
  ) => {
    if (!inputValue) return options;
    return matchSorter(options, inputValue, {
      keys: ['label', 'description'],
      threshold: matchSorter.rankings.CONTAINS,
    });
  };

  // Find the selected repo object from formData
  const selectedRepo = repos.find(repo => repo.value === formData) || null;

  const getHelperText = () => {
    if (error) return error;
    if (!loading && repos.length > 0) {
      return 'Search or select a repository from the univerus organization';
    }
    return undefined;
  };

  const getNoOptionsText = () => {
    if (loading) return 'Loading repositories...';
    if (error) return error;
    return 'No repositories found';
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <Autocomplete
        id="github-repo-picker-autocomplete"
        options={repos}
        value={selectedRepo}
        onChange={handleChange}
        filterOptions={filterOptions}
        getOptionLabel={option => option.label}
        getOptionSelected={(option, value) => option.value === value.value}
        loading={loading}
        disabled={loading}
        renderInput={params => (
          <TextField
            {...params}
            label="GitHub Repository"
            required={required}
            error={rawErrors?.length > 0 && !formData}
            helperText={getHelperText()}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={option => (
          <div style={{ width: '100%' }}>
            <div style={{ fontWeight: 500 }}>{option.label}</div>
            {option.description && (
              <div style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.54)' }}>
                {option.description}
              </div>
            )}
          </div>
        )}
        noOptionsText={getNoOptionsText()}
      />
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>This field is required</FormHelperText>
      )}
    </FormControl>
  );
};
